import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Moveable from 'react-moveable';
import { toast } from 'sonner';
import { Type, Image as ImageIcon, Shapes, Undo2, Redo2, ZoomIn, ZoomOut, Save, ArrowLeft, Eye, Loader2, Layers, SlidersHorizontal, Check, Palette, Group as GroupIcon, Ungroup as UngroupIcon, Copy, Trash2, Lock, LockOpen, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal, AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter } from 'lucide-react';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { createElement, uid, useHistory, normalizePages, createPage } from './editorState';
import { ElementContent, elementStyle } from './MenuCanvas';
import { PropertiesPanel, LayersPanel, uploadImage } from './EditorPanels';
import { ensureFontsForElements } from './fonts';
import { DownloadMenuButton } from './UserMenus';
import { ShapePicker } from './ShapePicker';
import { PageStrip } from './PageStrip';
import { RichTextToolbar } from './RichTextToolbar';
import { domToRuns } from './richText';

const rotatePt = (x, y, deg) => {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return { x: x * c - y * s, y: x * s + y * c };
};

const elementAABB = (el) => {
  const cx = el.x + el.w / 2, cy = el.y + el.h / 2;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  [[-el.w / 2, -el.h / 2], [el.w / 2, -el.h / 2], [el.w / 2, el.h / 2], [-el.w / 2, el.h / 2]].forEach(([dx, dy]) => {
    const p = rotatePt(dx, dy, el.rotation || 0);
    const px = cx + p.x, py = cy + p.y;
    minX = Math.min(minX, px); minY = Math.min(minY, py); maxX = Math.max(maxX, px); maxY = Math.max(maxY, py);
  });
  return { minX, minY, maxX, maxY };
};

const computeGroupBox = (members) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  members.forEach((el) => {
    const a = elementAABB(el);
    minX = Math.min(minX, a.minX); minY = Math.min(minY, a.minY);
    maxX = Math.max(maxX, a.maxX); maxY = Math.max(maxY, a.maxY);
  });
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY), rotation: 0 };
};

const ToolButton = ({ icon: Icon, label, onClick, testId, disabled, active }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`tap-none flex h-11 w-11 flex-col items-center justify-center gap-0.5 rounded-xl text-[#a1a1aa] transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-30 md:h-12 md:w-12 ${active ? 'bg-[#A855F7]/20 text-white' : ''}`}
  >
    <Icon className="h-5 w-5" />
    <span className="text-[9px] font-medium leading-none">{label}</span>
  </button>
);

const AlignBtn = ({ icon: Icon, label, onClick, testId, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className="flex h-9 flex-1 items-center justify-center rounded-lg border border-white/[0.08] text-[#a1a1aa] transition-colors hover:border-white/[0.2] hover:text-white disabled:opacity-25"
  >
    <Icon className="h-4 w-4" />
  </button>
);

const GroupPanel = ({ count, canGroup, canUngroup, locked, canDistribute, onGroup, onUngroup, onAlign, onDistribute, onToggleLock, onDuplicate, onDelete }) => (
  <div className="space-y-4 p-4" data-testid="group-properties">
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-[#71717a]">{count} elements selected</p>
      <button
        type="button"
        onClick={onToggleLock}
        data-testid="group-lock-toggle"
        title={locked ? 'Unlock' : 'Lock'}
        className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-[11px] font-semibold ${locked ? 'bg-[#EAB308]/20 text-[#EAB308]' : 'text-[#a1a1aa] hover:bg-white/[0.06] hover:text-white'}`}
      >
        {locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
        {locked ? 'Locked' : 'Lock'}
      </button>
    </div>

    {locked ? (
      <p className="rounded-lg bg-[#EAB308]/10 px-3 py-2 text-xs leading-relaxed text-[#EAB308]/90">This block is locked to prevent accidental changes. Tap <span className="font-semibold">Locked</span> above to unlock it.</p>
    ) : (
      <>
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#71717a]">Align</p>
          <div className="flex gap-1.5">
            <AlignBtn icon={AlignStartVertical} label="Align left" testId="align-left-btn" onClick={() => onAlign('left')} />
            <AlignBtn icon={AlignCenterVertical} label="Align horizontal centers" testId="align-center-h-btn" onClick={() => onAlign('centerH')} />
            <AlignBtn icon={AlignEndVertical} label="Align right" testId="align-right-btn" onClick={() => onAlign('right')} />
            <span className="w-px bg-white/[0.08]" />
            <AlignBtn icon={AlignStartHorizontal} label="Align top" testId="align-top-btn" onClick={() => onAlign('top')} />
            <AlignBtn icon={AlignCenterHorizontal} label="Align vertical centers" testId="align-middle-v-btn" onClick={() => onAlign('middleV')} />
            <AlignBtn icon={AlignEndHorizontal} label="Align bottom" testId="align-bottom-btn" onClick={() => onAlign('bottom')} />
          </div>
        </div>
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#71717a]">Distribute spacing</p>
          <div className="flex gap-1.5">
            <AlignBtn icon={AlignHorizontalDistributeCenter} label="Distribute horizontally" testId="distribute-h-btn" disabled={!canDistribute} onClick={() => onDistribute('h')} />
            <AlignBtn icon={AlignVerticalDistributeCenter} label="Distribute vertically" testId="distribute-v-btn" disabled={!canDistribute} onClick={() => onDistribute('v')} />
          </div>
          {!canDistribute && <p className="mt-1 text-[10px] text-[#52525b]">Select 3+ elements to distribute.</p>}
        </div>
      </>
    )}

    <div className="flex gap-2">
      {canGroup && (
        <button type="button" data-testid="group-panel-group-btn" onClick={onGroup} className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[#A855F7]/20 text-sm font-semibold text-white hover:bg-[#A855F7]/30">
          <GroupIcon className="h-4 w-4" /> Group
        </button>
      )}
      {canUngroup && (
        <button type="button" data-testid="group-panel-ungroup-btn" onClick={onUngroup} className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-white/[0.1] text-sm font-semibold text-[#a1a1aa] hover:text-white">
          <UngroupIcon className="h-4 w-4" /> Ungroup
        </button>
      )}
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={onDuplicate} className="flex h-9 flex-1 items-center justify-center gap-2 rounded-lg border border-white/[0.08] text-sm text-[#a1a1aa] hover:text-white"><Copy className="h-4 w-4" /> Duplicate</button>
      <button type="button" onClick={onDelete} disabled={locked} className="flex h-9 flex-1 items-center justify-center gap-2 rounded-lg border border-white/[0.08] text-sm text-[#a1a1aa] hover:text-[#f43f5e] disabled:opacity-30"><Trash2 className="h-4 w-4" /> Delete</button>
    </div>
  </div>
);

export const MenuEditor = ({ menu, fonts, onSave, onBack, onPreview, saving, lastSavedAt, status, onToggleStatus }) => {
  const isDesktop = useIsDesktop();
  const initialRef = useRef(null);
  if (!initialRef.current) initialRef.current = { canvas: menu.canvas, pages: normalizePages(menu) };
  const { state, update, commit, undo, redo, canUndo, canRedo } = useHistory(initialRef.current);
  const [title, setTitle] = useState(menu.title);
  const [activePageId, setActivePageId] = useState(initialRef.current.pages[0].id);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [mobilePanel, setMobilePanel] = useState(null);
  const [showShapes, setShowShapes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [groupBox, setGroupBox] = useState(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const moveableRef = useRef(null);
  const groupBoxRef = useRef(null);
  const fileRef = useRef(null);
  const dirty = useRef(false);
  const [targetEl, setTargetEl] = useState(null);
  const pendingDrag = useRef(null);
  const grpSnap = useRef(null);
  const interacting = useRef(false);

  const { canvas, pages } = state;
  const activePage = pages.find((p) => p.id === activePageId) || pages[0];
  const activeId = activePage.id;
  const elements = activePage.elements;
  const isMulti = selectedIds.length > 1;
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const selected = elements.find((e) => e.id === selectedId) || null;

  const selectedEls = useMemo(() => elements.filter((e) => selectedIds.includes(e.id)), [elements, selectedIds]);
  const canGroup = useMemo(() => {
    if (selectedIds.length < 2) return false;
    const gids = new Set(selectedEls.map((e) => e.groupId || null));
    return !(gids.size === 1 && !gids.has(null));
  }, [selectedIds, selectedEls]);
  const canUngroup = useMemo(() => selectedEls.some((e) => e.groupId), [selectedEls]);
  const selectionLocked = selectedEls.length > 0 && selectedEls.every((e) => e.locked);

  useEffect(() => { ensureFontsForElements(elements, fonts.customFonts); }, [elements, fonts.customFonts]);

  useEffect(() => {
    const fit = () => {
      const w = stageRef.current?.clientWidth || 800;
      setZoom(Math.min(1, Math.max(0.3, (w - 32) / canvas.width)));
    };
    fit();
  }, [canvas.width]);

  const syncMoveable = () => requestAnimationFrame(() => moveableRef.current?.updateRect());
  useEffect(() => { const id = requestAnimationFrame(() => moveableRef.current?.updateRect()); return () => cancelAnimationFrame(id); }, [zoom, targetEl]);

  const markDirty = () => { dirty.current = true; };
  const setActiveElements = useCallback((mapper, opts) => {
    markDirty();
    update((s) => ({ ...s, pages: s.pages.map((p) => (p.id === activeId ? { ...p, elements: mapper(p.elements) } : p)) }), opts);
  }, [update, activeId]);
  const patchElement = useCallback((id, patch, opts) => {
    setActiveElements((els) => els.map((e) => (e.id === id ? { ...e, ...patch } : e)), opts);
    if (opts?.record !== false) requestAnimationFrame(() => moveableRef.current?.updateRect());
  }, [setActiveElements]);
  const patchCanvas = (patch) => { markDirty(); update((s) => ({ ...s, canvas: { ...s.canvas, ...patch } })); };

  const getEditingRoot = useCallback(() => (editingId ? canvasRef.current?.querySelector(`[data-el-id="${editingId}"] [data-rich-text]`) : null), [editingId]);
  const endEditing = useCallback(() => {
    const root = getEditingRoot();
    if (root && editingId) patchElement(editingId, domToRuns(root));
    setEditingId(null);
  }, [getEditingRoot, editingId, patchElement]);

  const addElement = (type, extra) => {
    const el = createElement(type, canvas, extra);
    setActiveElements((els) => [...els, el]);
    setSelectedIds([el.id]);
    if (!isDesktop) setMobilePanel('props');
  };

  const deleteElement = (id) => { setActiveElements((els) => els.filter((e) => e.id !== id)); setSelectedIds((ids) => ids.filter((x) => x !== id)); };
  const duplicateElement = (id) => {
    const src = elements.find((e) => e.id === id);
    if (!src) return;
    // Duplicating any grouped element copies the entire group and keeps the copies grouped.
    if (src.groupId) {
      const members = elements.filter((e) => e.groupId === src.groupId);
      const gid = uid();
      const copies = members.map((m) => ({ ...m, id: uid(), x: m.x + 16, y: m.y + 16, groupId: gid, name: `${m.name || m.type} copy` }));
      setActiveElements((els) => [...els, ...copies]);
      setSelectedIds(copies.map((c) => c.id));
      return;
    }
    const copy = { ...src, id: uid(), x: src.x + 16, y: src.y + 16, groupId: null, name: `${src.name || src.type} copy` };
    setActiveElements((els) => [...els, copy]);
    setSelectedIds([copy.id]);
  };

  const deleteSelected = () => {
    if (!selectedIds.length) return;
    setActiveElements((els) => els.filter((e) => !selectedIds.includes(e.id)));
    setSelectedIds([]);
  };
  const duplicateSelected = () => {
    if (!selectedIds.length) return;
    const srcs = elements.filter((e) => selectedIds.includes(e.id));
    const gmap = {};
    const copies = srcs.map((src) => {
      const c = { ...src, id: uid(), x: src.x + 16, y: src.y + 16, name: `${src.name || src.type} copy` };
      // Preserve grouping: grouped originals stay grouped as copies, ungrouped stay ungrouped.
      if (src.groupId) { gmap[src.groupId] = gmap[src.groupId] || uid(); c.groupId = gmap[src.groupId]; }
      else c.groupId = null;
      return c;
    });
    setActiveElements((els) => [...els, ...copies]);
    setSelectedIds(copies.map((c) => c.id));
  };

  const groupSelected = () => {
    if (selectedIds.length < 2) return;
    const gid = uid();
    setActiveElements((els) => els.map((e) => (selectedIds.includes(e.id) ? { ...e, groupId: gid } : e)));
    toast.success('Elements grouped');
  };
  const ungroupSelected = () => {
    setActiveElements((els) => els.map((e) => (selectedIds.includes(e.id) ? { ...e, groupId: null } : e)));
    toast.success('Group removed');
  };

  const toggleLockSelected = () => {
    const lock = !selectionLocked;
    setActiveElements((els) => els.map((e) => (selectedIds.includes(e.id) ? { ...e, locked: lock } : e)));
    toast.success(lock ? 'Locked' : 'Unlocked');
  };

  const alignSelected = (mode) => {
    if (selectedIds.length < 2) return;
    const sel = elements.filter((e) => selectedIds.includes(e.id));
    const boxes = {};
    sel.forEach((e) => { boxes[e.id] = elementAABB(e); });
    const gMinX = Math.min(...sel.map((e) => boxes[e.id].minX));
    const gMaxX = Math.max(...sel.map((e) => boxes[e.id].maxX));
    const gMinY = Math.min(...sel.map((e) => boxes[e.id].minY));
    const gMaxY = Math.max(...sel.map((e) => boxes[e.id].maxY));
    const gcx = (gMinX + gMaxX) / 2, gcy = (gMinY + gMaxY) / 2;
    const deltas = {};
    sel.forEach((e) => {
      const b = boxes[e.id];
      let dx = 0, dy = 0;
      if (mode === 'left') dx = gMinX - b.minX;
      else if (mode === 'right') dx = gMaxX - b.maxX;
      else if (mode === 'centerH') dx = gcx - (b.minX + b.maxX) / 2;
      else if (mode === 'top') dy = gMinY - b.minY;
      else if (mode === 'bottom') dy = gMaxY - b.maxY;
      else if (mode === 'middleV') dy = gcy - (b.minY + b.maxY) / 2;
      deltas[e.id] = { dx, dy };
    });
    setActiveElements((els) => els.map((e) => (deltas[e.id] ? { ...e, x: e.x + deltas[e.id].dx, y: e.y + deltas[e.id].dy } : e)));
  };

  const distributeSelected = (axis) => {
    if (selectedIds.length < 3) return;
    const sel = elements.filter((e) => selectedIds.includes(e.id));
    const boxes = {};
    sel.forEach((e) => { boxes[e.id] = elementAABB(e); });
    const lo = axis === 'h' ? 'minX' : 'minY';
    const hi = axis === 'h' ? 'maxX' : 'maxY';
    const center = (id) => (boxes[id][lo] + boxes[id][hi]) / 2;
    const sorted = [...sel].sort((a, b) => center(a.id) - center(b.id));
    const spanStart = boxes[sorted[0].id][lo];
    const spanEnd = boxes[sorted[sorted.length - 1].id][hi];
    const totalSize = sorted.reduce((sum, e) => sum + (boxes[e.id][hi] - boxes[e.id][lo]), 0);
    const gap = (spanEnd - spanStart - totalSize) / (sorted.length - 1);
    const deltas = {};
    let cursor = spanStart;
    sorted.forEach((e) => {
      const b = boxes[e.id];
      const d = cursor - b[lo];
      deltas[e.id] = axis === 'h' ? { dx: d, dy: 0 } : { dx: 0, dy: d };
      cursor += (b[hi] - b[lo]) + gap;
    });
    setActiveElements((els) => els.map((e) => (deltas[e.id] ? { ...e, x: e.x + deltas[e.id].dx, y: e.y + deltas[e.id].dy } : e)));
  };

  const reorder = (id, dir) => {
    setActiveElements((els) => {
      const arr = [...els];
      const i = arr.findIndex((e) => e.id === id);
      const j = i + dir;
      if (j < 0 || j >= arr.length) return els;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  // ── Pages ──
  const selectPage = (id) => { setActivePageId(id); setSelectedIds([]); setEditingId(null); };
  const addPage = () => {
    markDirty();
    const np = createPage();
    update((s) => {
      const i = s.pages.findIndex((p) => p.id === activeId);
      const pgs = [...s.pages];
      pgs.splice(i + 1, 0, np);
      return { ...s, pages: pgs };
    });
    setActivePageId(np.id); setSelectedIds([]); setEditingId(null);
    toast.success('Page added');
  };
  const duplicatePage = () => {
    markDirty();
    const src = pages.find((p) => p.id === activeId);
    const gmap = {};
    const els = src.elements.map((e) => {
      const c = { ...e, id: uid() };
      if (e.groupId) { gmap[e.groupId] = gmap[e.groupId] || uid(); c.groupId = gmap[e.groupId]; }
      return c;
    });
    const np = { id: uid(), elements: els };
    update((s) => {
      const i = s.pages.findIndex((p) => p.id === activeId);
      const pgs = [...s.pages];
      pgs.splice(i + 1, 0, np);
      return { ...s, pages: pgs };
    });
    setActivePageId(np.id); setSelectedIds([]); setEditingId(null);
    toast.success('Page duplicated');
  };
  const deletePage = () => {
    if (pages.length <= 1) { toast.error('A menu needs at least one page'); return; }
    markDirty();
    const i = pages.findIndex((p) => p.id === activeId);
    const nextId = pages[i - 1]?.id || pages[i + 1]?.id;
    update((s) => ({ ...s, pages: s.pages.filter((p) => p.id !== activeId) }));
    setActivePageId(nextId); setSelectedIds([]); setEditingId(null);
  };
  const movePage = (dir) => {
    markDirty();
    update((s) => {
      const i = s.pages.findIndex((p) => p.id === activeId);
      const j = i + dir;
      if (j < 0 || j >= s.pages.length) return s;
      const pgs = [...s.pages];
      [pgs[i], pgs[j]] = [pgs[j], pgs[i]];
      return { ...s, pages: pgs };
    });
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const img = new Image();
      img.onload = () => {
        const maxW = canvas.width - 40;
        const w = Math.min(maxW, img.naturalWidth);
        const h = Math.round(w * (img.naturalHeight / img.naturalWidth));
        addElement('image', { src: res.url, w, h, x: (canvas.width - w) / 2, name: file.name.replace(/\.[^.]+$/, '') });
      };
      img.onerror = () => addElement('image', { src: res.url });
      img.src = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = useCallback(async () => {
    await onSave({ title, canvas, pages });
    dirty.current = false;
  }, [onSave, title, canvas, pages]);

  // Autosave 2s after last change
  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(() => { if (dirty.current) save(); }, 2000);
    return () => clearTimeout(t);
  }, [pages, canvas, title, save]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || document.activeElement?.isContentEditable;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); save(); return; }
      if (typing) return;
      if (mod && e.key.toLowerCase() === 'g') { e.preventDefault(); e.shiftKey ? ungroupSelected() : groupSelected(); return; }
      if (!selectedIds.length) return;
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateSelected(); return; }
      if (selectionLocked) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected(); }
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const d = { ArrowUp: { y: -step }, ArrowDown: { y: step }, ArrowLeft: { x: -step }, ArrowRight: { x: step } }[e.key];
        if (d) { setActiveElements((els) => els.map((el) => (selectedIds.includes(el.id) ? { ...el, x: el.x + (d.x || 0), y: el.y + (d.y || 0) } : el))); syncMoveable(); }
      }
      if (e.key === 'Escape') { setSelectedIds([]); setEditingId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Recompute the group bounding box when selection or geometry changes (skipped while interacting).
  useEffect(() => {
    if (selectedIds.length < 2) { setGroupBox(null); return; }
    if (interacting.current) return;
    setGroupBox(computeGroupBox(elements.filter((e) => selectedIds.includes(e.id))));
  }, [selectedIds, elements]);

  useEffect(() => {
    if (isMulti) { setTargetEl(selectionLocked ? null : (groupBoxRef.current || null)); return; }
    const node = selected && !selected.locked && !editingId ? canvasRef.current?.querySelector(`[data-el-id="${selected.id}"]`) : null;
    setTargetEl(node || null);
  }, [selectedIds, selected?.locked, editingId, elements.length, zoom, groupBox, isMulti, selectionLocked]);

  const armDrag = (nativeEvent) => {
    pendingDrag.current = nativeEvent;
    const clear = () => { pendingDrag.current = null; window.removeEventListener('mouseup', clear); window.removeEventListener('touchend', clear); };
    window.addEventListener('mouseup', clear);
    window.addEventListener('touchend', clear);
  };

  useEffect(() => {
    if (targetEl && pendingDrag.current && moveableRef.current) {
      const ev = pendingDrag.current;
      pendingDrag.current = null;
      requestAnimationFrame(() => { try { moveableRef.current?.dragStart(ev); } catch (e) { /* ignore */ } });
    }
  }, [targetEl]);

  const handleMemberPointerDown = (e, el) => {
    e.stopPropagation();
    if (editingId === el.id) return;
    if (e.shiftKey) {
      setSelectedIds((ids) => (ids.includes(el.id) ? ids.filter((x) => x !== el.id) : [...ids, el.id]));
      endEditing();
      return;
    }
    const groupIds = el.groupId ? elements.filter((x) => x.groupId === el.groupId).map((x) => x.id) : [el.id];
    const same = groupIds.length === selectedIds.length && groupIds.every((id) => selectedIds.includes(id));
    if (!same) {
      const anyLocked = elements.filter((x) => groupIds.includes(x.id)).some((x) => x.locked);
      if (!anyLocked) armDrag(e.nativeEvent);
      setSelectedIds(groupIds);
      if (!isDesktop && groupIds.length > 1) setMobilePanel('props');
    }
    endEditing();
  };

  // --- Group transform (drag / resize / rotate together) ---
  const beginGroup = () => {
    commit();
    interacting.current = true;
    grpSnap.current = { refBox: { ...groupBox }, members: elements.filter((e) => selectedIds.includes(e.id)).map((e) => ({ ...e })) };
  };
  const applyGroup = (curBox) => {
    const snap = grpSnap.current;
    if (!snap?.refBox) return;
    const ref = snap.refBox;
    const s = ref.w ? curBox.w / ref.w : 1;
    const dRot = (curBox.rotation || 0) - (ref.rotation || 0);
    const refC = { x: ref.x + ref.w / 2, y: ref.y + ref.h / 2 };
    const curC = { x: curBox.x + curBox.w / 2, y: curBox.y + curBox.h / 2 };
    const patches = {};
    snap.members.forEach((sm) => {
      const mc = { x: sm.x + sm.w / 2, y: sm.y + sm.h / 2 };
      const q = rotatePt(mc.x - refC.x, mc.y - refC.y, -(ref.rotation || 0));
      const uv = { u: q.x / ref.w, v: q.y / ref.h };
      const q2 = rotatePt(uv.u * curBox.w, uv.v * curBox.h, curBox.rotation || 0);
      const nc = { x: curC.x + q2.x, y: curC.y + q2.y };
      const nw = sm.w * s, nh = sm.h * s;
      const p = { x: nc.x - nw / 2, y: nc.y - nh / 2, w: nw, h: nh, rotation: (sm.rotation || 0) + dRot };
      if (sm.type === 'text') { p.fontSize = Math.max(6, (sm.fontSize || 24) * s); if (sm.letterSpacing) p.letterSpacing = sm.letterSpacing * s; }
      if (sm.strokeWidth) p.strokeWidth = sm.strokeWidth * s;
      if (sm.radius) p.radius = sm.radius * s;
      patches[sm.id] = p;
    });
    setActiveElements((els) => els.map((e) => (patches[e.id] ? { ...e, ...patches[e.id] } : e)), { record: false });
    setGroupBox(curBox);
  };
  const endGroup = () => {
    interacting.current = false;
    grpSnap.current = null;
    requestAnimationFrame(() => { syncMoveable(); setSelectedIds((ids) => [...ids]); });
  };

  const guidelines = useMemo(() => elements.filter((e) => !selectedIds.includes(e.id) && !e.hidden).map((e) => `[data-el-id="${e.id}"]`), [elements, selectedIds]);

  const panelProps = isMulti ? (
    <GroupPanel count={selectedIds.length} canGroup={canGroup} canUngroup={canUngroup} locked={selectionLocked} canDistribute={selectedIds.length >= 3} onGroup={groupSelected} onUngroup={ungroupSelected} onAlign={alignSelected} onDistribute={distributeSelected} onToggleLock={toggleLockSelected} onDuplicate={duplicateSelected} onDelete={deleteSelected} />
  ) : (
    <PropertiesPanel el={selected} canvas={canvas} fonts={fonts} onChange={(patch) => patchElement(selected.id, patch)} onCanvasChange={patchCanvas} />
  );
  const panelLayers = (
    <LayersPanel
      elements={elements}
      selectedIds={selectedIds}
      onSelect={(id) => setSelectedIds([id])}
      onToggleSelect={(id) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))}
      onChange={patchElement}
      onReorder={reorder}
      onDelete={deleteElement}
      onDuplicate={duplicateElement}
      canGroup={canGroup}
      canUngroup={canUngroup}
      onGroup={groupSelected}
      onUngroup={ungroupSelected}
    />
  );

  const toolbar = (
    <>
      <ToolButton icon={Type} label="Text" testId="add-text-btn" onClick={() => addElement('text')} />
      <ToolButton icon={uploading ? Loader2 : ImageIcon} label="Image" testId="add-image-btn" disabled={uploading} onClick={() => fileRef.current?.click()} />
      <ToolButton icon={Shapes} label="Shapes" testId="add-shape-btn" active={showShapes} onClick={() => { setShowShapes((v) => !v); if (!isDesktop) setMobilePanel(null); }} />
      <ToolButton icon={Palette} label="Page" testId="page-settings-btn" active={!selectedIds.length && (isDesktop || mobilePanel === 'props')} onClick={() => { setSelectedIds([]); if (!isDesktop) setMobilePanel('props'); }} />
      {!isDesktop && <ToolButton icon={Layers} label="Layers" testId="layers-btn" active={mobilePanel === 'layers'} onClick={() => setMobilePanel(mobilePanel === 'layers' ? null : 'layers')} />}
      {!isDesktop && (selected || isMulti) && <ToolButton icon={SlidersHorizontal} label="Edit" testId="props-btn" active={mobilePanel === 'props'} onClick={() => setMobilePanel(mobilePanel === 'props' ? null : 'props')} />}
    </>
  );

  return (
    <div className="relative flex h-[100dvh] flex-col bg-[#09090b] text-white" data-testid="menu-editor">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} data-testid="image-file-input" />

      {/* Top bar */}
      <div className="pt-safe flex items-center gap-2 border-b border-white/[0.06] bg-[#0f0f11] px-3 py-2">
        <button type="button" onClick={onBack} data-testid="editor-back-btn" className="tap-none flex h-9 w-9 items-center justify-center rounded-full text-[#a1a1aa] hover:bg-white/[0.06] hover:text-white" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
        <input
          value={title}
          onChange={(e) => { setTitle(e.target.value); markDirty(); }}
          data-testid="menu-title-input"
          className="h-9 min-w-0 flex-1 rounded-lg bg-transparent px-2 font-heading text-sm font-semibold text-white focus:bg-white/[0.04] focus:outline-none md:max-w-xs"
        />
        {isDesktop && <div className="flex items-center gap-1">
          <button type="button" onClick={undo} disabled={!canUndo} data-testid="undo-btn" className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a1a1aa] hover:bg-white/[0.06] disabled:opacity-30" title="Undo"><Undo2 className="h-4 w-4" /></button>
          <button type="button" onClick={redo} disabled={!canRedo} data-testid="redo-btn" className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a1a1aa] hover:bg-white/[0.06] disabled:opacity-30" title="Redo"><Redo2 className="h-4 w-4" /></button>
          <span className="mx-1 h-5 w-px bg-white/[0.08]" />
          <button type="button" onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))} className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a1a1aa] hover:bg-white/[0.06]" title="Zoom out"><ZoomOut className="h-4 w-4" /></button>
          <span className="w-12 text-center text-xs tabular text-[#a1a1aa]" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => Math.min(2, z + 0.1))} className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a1a1aa] hover:bg-white/[0.06]" title="Zoom in"><ZoomIn className="h-4 w-4" /></button>
        </div>}
        <span className="hidden text-xs text-[#52525b] sm:block" data-testid="save-status">
          {saving ? 'Saving…' : lastSavedAt ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-[#10b981]" /> Saved</span> : ''}
        </span>
        <button
          type="button"
          onClick={onToggleStatus}
          data-testid="editor-status-toggle"
          title={status === 'published' ? 'Published — tap to unpublish' : 'Draft — tap to publish'}
          className={`tap-none flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-semibold ${status === 'published' ? 'bg-[#10b981]/20 text-[#10b981]' : 'bg-white/[0.06] text-[#a1a1aa]'}`}
        >
          <span className={`h-2 w-2 rounded-full ${status === 'published' ? 'bg-[#10b981]' : 'bg-[#71717a]'}`} />
          <span className="hidden sm:inline">{status === 'published' ? 'Published' : 'Draft'}</span>
        </button>
        <DownloadMenuButton menu={{ title, canvas, elements }} customFonts={fonts.customFonts} className="border border-white/[0.1] !bg-transparent text-[#a1a1aa] hover:text-white" testId="editor-download-btn" />
        <button type="button" onClick={() => onPreview({ title, canvas, pages })} data-testid="preview-menu-btn" className="tap-none flex h-9 items-center gap-1.5 rounded-full border border-white/[0.1] px-3 text-xs font-semibold text-[#a1a1aa] hover:text-white"><Eye className="h-4 w-4" /><span className="hidden sm:inline">Preview</span></button>
        <button type="button" onClick={save} disabled={saving} data-testid="save-menu-btn" className="tap-none flex h-9 items-center gap-1.5 rounded-full bg-[#EAB308] px-3.5 text-xs font-semibold text-black hover:bg-[#CA8A04] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}<span>Save</span>
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1">
        {isDesktop && <div className="relative z-30 flex w-16 flex-col items-center gap-1 border-r border-white/[0.06] bg-[#0f0f11] py-3">{toolbar}</div>}
        {isDesktop && showShapes && (
          <ShapePicker
            className="absolute left-16 top-0 z-30 h-full w-80 border-r border-white/[0.08] shadow-2xl"
            onClose={() => setShowShapes(false)}
            onAdd={(type, extra) => addElement(type, extra)}
          />
        )}

        {/* Stage + page strip */}
        <div className="relative flex min-w-0 flex-1 flex-col">
        <div
          ref={stageRef}
          className="relative flex-1 overflow-auto"
          style={{ backgroundColor: '#141416', backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '16px 16px' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget || e.target === canvasRef.current) { setSelectedIds([]); endEditing(); } }}
          onTouchStart={(e) => { if (e.target === e.currentTarget || e.target === canvasRef.current) { setSelectedIds([]); endEditing(); } }}
          data-testid="editor-stage"
        >
          {(canGroup || canUngroup) && (
            <div className="absolute left-1/2 top-2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/[0.1] bg-[#0f0f11]/95 px-1 py-1 shadow-xl backdrop-blur">
              {canGroup && (
                <button type="button" data-testid="group-btn" onClick={groupSelected} className="flex h-8 items-center gap-1.5 rounded-full bg-[#A855F7]/20 px-3 text-xs font-semibold text-white hover:bg-[#A855F7]/30">
                  <GroupIcon className="h-3.5 w-3.5" /> Group
                </button>
              )}
              {canUngroup && (
                <button type="button" data-testid="ungroup-btn" onClick={ungroupSelected} className="flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-[#a1a1aa] hover:bg-white/[0.06] hover:text-white">
                  <UngroupIcon className="h-3.5 w-3.5" /> Ungroup
                </button>
              )}
            </div>
          )}
          <div className="flex min-h-full items-start justify-center p-4 pb-32 md:p-8">
            <div
              ref={canvasRef}
              className="relative shadow-2xl"
              data-testid="editor-canvas"
              style={{ width: canvas.width * zoom, height: canvas.height * zoom, background: canvas.background, flexShrink: 0 }}
            >
              {elements.map((el) => {
                const isSel = selectedIds.includes(el.id);
                const outline = isSel && isMulti ? '1px solid rgba(168,85,247,0.9)'
                  : (isSel && el.locked ? '1px dashed #EAB308' : undefined);
                return (
                  <div
                    key={el.id}
                    data-el-id={el.id}
                    style={{ ...elementStyle(el, zoom), cursor: el.locked ? 'default' : 'move', outline }}
                    onMouseDown={(e) => handleMemberPointerDown(e, el)}
                    onTouchStart={(e) => handleMemberPointerDown(e, el)}
                    onDoubleClick={() => { if (el.type === 'text' && !el.locked && !isMulti) { setEditingId(el.id); } }}
                    onClick={() => { if (!isDesktop && el.type === 'text' && selectedId === el.id && !el.locked) setEditingId(el.id); }}
                  >
                    <ElementContent
                      el={el}
                      scale={zoom}
                      editing={editingId === el.id}
                      onTextCommit={(patch) => { patchElement(el.id, patch); setEditingId(null); }}
                    />
                  </div>
                );
              })}
              {editingId && elements.find((e) => e.id === editingId) && (
                <RichTextToolbar el={elements.find((e) => e.id === editingId)} zoom={zoom} getRoot={getEditingRoot} onDone={endEditing} />
              )}
              {isMulti && groupBox && (
                <div
                  ref={groupBoxRef}
                  data-testid="group-box"
                  style={{ position: 'absolute', left: groupBox.x * zoom, top: groupBox.y * zoom, width: groupBox.w * zoom, height: groupBox.h * zoom, transform: `rotate(${groupBox.rotation || 0}deg)`, cursor: selectionLocked ? 'default' : 'move', background: 'transparent', outline: selectionLocked ? '1.5px dashed #EAB308' : undefined }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                />
              )}
              {elements.length === 0 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8 text-center text-sm text-white/40">
                  Add text, images and shapes from the toolbar, then drag them anywhere.
                </div>
              )}
            </div>
          </div>

          {targetEl && isMulti && (
            <Moveable
              ref={moveableRef}
              target={targetEl}
              origin={false}
              draggable
              resizable
              rotatable
              keepRatio
              renderDirections={['nw', 'ne', 'sw', 'se']}
              throttleDrag={0}
              throttleResize={0}
              throttleRotate={0}
              onDragStart={beginGroup}
              onDrag={(e) => applyGroup({ ...grpSnap.current.refBox, x: e.left / zoom, y: e.top / zoom })}
              onDragEnd={endGroup}
              onResizeStart={beginGroup}
              onResize={(e) => {
                // Sync the box DOM immediately so moveable reads the correct size next frame.
                e.target.style.width = `${e.width}px`;
                e.target.style.height = `${e.height}px`;
                e.target.style.left = `${e.drag.left}px`;
                e.target.style.top = `${e.drag.top}px`;
                applyGroup({ x: e.drag.left / zoom, y: e.drag.top / zoom, w: e.width / zoom, h: e.height / zoom, rotation: grpSnap.current.refBox.rotation });
              }}
              onResizeEnd={endGroup}
              onRotateStart={beginGroup}
              onRotate={(e) => applyGroup({ ...grpSnap.current.refBox, rotation: (e.rotation ?? e.rotate) })}
              onRotateEnd={endGroup}
            />
          )}

          {targetEl && !isMulti && (
            <Moveable
              ref={moveableRef}
              target={targetEl}
              origin={false}
              draggable
              resizable
              rotatable
              keepRatio={selected?.type === 'image' || selected?.type === 'ellipse'}
              snappable
              snapThreshold={5}
              elementGuidelines={guidelines}
              verticalGuidelines={[0, (canvas.width * zoom) / 2, canvas.width * zoom]}
              horizontalGuidelines={[0, (canvas.height * zoom) / 2, canvas.height * zoom]}
              isDisplaySnapDigit={false}
              throttleDrag={0}
              throttleResize={0}
              throttleRotate={0}
              renderDirections={['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w']}
              onDragStart={commit}
              onDrag={(e) => patchElement(selected.id, { x: e.left / zoom, y: e.top / zoom }, { record: false })}
              onResizeStart={commit}
              onResizeEnd={(e) => { const l = e.lastEvent; if (l) patchElement(selected.id, { w: l.width / zoom, h: l.height / zoom, x: l.drag.left / zoom, y: l.drag.top / zoom }, { record: false }); syncMoveable(); }}
              onDragEnd={syncMoveable}
              onRotateEnd={syncMoveable}
              onResize={(e) => { e.target.style.width = `${e.width}px`; e.target.style.height = `${e.height}px`; e.target.style.left = `${e.drag.left}px`; e.target.style.top = `${e.drag.top}px`; }}
              onRotateStart={commit}
              onRotate={(e) => patchElement(selected.id, { rotation: Math.round(((e.rotation ?? e.rotate) % 360) * 10) / 10 }, { record: false })}
            />
          )}
        </div>
          <PageStrip
            pages={pages}
            activePageId={activeId}
            canvas={canvas}
            customFonts={fonts.customFonts}
            onSelect={selectPage}
            onAdd={addPage}
            onDuplicate={duplicatePage}
            onDelete={deletePage}
            onMove={movePage}
          />
        </div>

        {isDesktop && (
          <div className="flex w-72 flex-col border-l border-white/[0.06] bg-[#0f0f11]">
            <div className="min-h-0 flex-1 overflow-y-auto border-b border-white/[0.06]">{panelProps}</div>
            <div className="max-h-[40%] overflow-y-auto">{panelLayers}</div>
          </div>
        )}
      </div>

      {/* Mobile toolbar + sheet */}
      {!isDesktop && (
        <>
          {showShapes && (
            <ShapePicker
              className="absolute inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-30 h-[55dvh] rounded-t-2xl border-t border-white/[0.08] shadow-2xl"
              onClose={() => setShowShapes(false)}
              onAdd={(type, extra) => { addElement(type, extra); setShowShapes(false); }}
            />
          )}
          {mobilePanel && !showShapes && (
            <div className="absolute inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-30 max-h-[50dvh] overflow-y-auto rounded-t-2xl border-t border-white/[0.08] bg-[#0f0f11] shadow-2xl" data-testid="mobile-panel">
              {mobilePanel === 'layers' ? panelLayers : panelProps}
            </div>
          )}
          <div className="pb-safe z-40 flex items-center justify-around border-t border-white/[0.06] bg-[#0f0f11] px-1 py-1.5">
            {toolbar}
            <ToolButton icon={Undo2} label="Undo" onClick={undo} disabled={!canUndo} testId="undo-btn" />
          </div>
        </>
      )}
    </div>
  );
};
